import { selectG2ReviewEnabled } from 'app/shared/featureFlags/selectors';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import {
  U21Alert,
  U21Button,
  U21ExternalLinkModal,
  U21MenuLayout,
  U21Modal,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAgent } from 'app/modules/session/selectors';
import styled from 'styled-components';
import { useGenerateG2State } from 'app/modules/g2/queries/useGetG2State';
import { sendErrorToast } from 'app/shared/toasts/actions';
import G2SVG from 'app/shared/svg/g2.svg';
import { Backdrop } from '@mui/material';
import { createPortal } from 'react-dom';

export const G2ReviewIndicator = () => {
  const dispatch = useDispatch();
  const g2ReviewEnabled = useSelector(selectG2ReviewEnabled);

  const { email } = useSelector(selectAgent);

  const [popupOpen, setPopupOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [state, setState] = useState<string>('');

  const { isPending, mutateAsync: generateG2State } = useGenerateG2State();

  const [timestamp, setTimestamp] = useLocalStorageState<string>(
    LocalStorageKeys.G2_REVIEW_LAST_OPEN_TIME,
    // treat empty as really old date so modal is opened
    new Date(0).toISOString(),
  );

  useEffect(() => {
    if (!g2ReviewEnabled) {
      return;
    }
    const date = new Date(timestamp);

    // treat invalid date as really old date so modal is opened
    if (isNaN(date.getTime())) {
      setPopupOpen(true);
      return;
    }
    const diff = differenceInDays(new Date(), date);
    if (diff > 2) {
      setPopupOpen(true);
    }
  }, [g2ReviewEnabled, timestamp]);

  if (!g2ReviewEnabled) {
    return null;
  }

  return (
    <>
      {/* unfortunately mui does not allow overriding invisible: false backdrop so render backdrop separately */}
      {popupOpen &&
        createPortal(<StyledBackdrop open={popupOpen} />, document.body)}
      <U21MenuLayout
        alignRight
        disableEscapeKeyDown
        onClose={() => setPopupOpen(false)}
        open={popupOpen}
        trigger={
          <U21Button
            aria-label="Tell us what you think!"
            icon={<G2SVG height={20} width={20} />}
            onClick={() => setPopupOpen(true)}
            tooltip="Tell us what you think!"
          />
        }
        backdropProps={{
          // disables backdrop click
          onClick: () => {},
        }}
      >
        <Content>
          <U21Typography variant="body2">
            <b>👋 Enjoying Unit21?</b> Write a Unit21 review on G2!
            <br />
            <br />⚡ This will take approximately 5 minutes.
          </U21Typography>
          <StyledU21Alert Icon={() => '✏️'} severity="info">
            Write a review by <b>May 23rd</b> and get $100! 💰
          </StyledU21Alert>
          <StyledU21Typography variant="caption">
            Before choosing to submit a review, you can review G2&apos;s{' '}
            <U21ExternalLinkModal url="https://legal.g2.com/privacy-policy">
              {({ onClick }) => (
                <U21Button color="primary" onClick={onClick} variant="text">
                  Privacy Policy
                </U21Button>
              )}
            </U21ExternalLinkModal>{' '}
            and{' '}
            <U21ExternalLinkModal url="https://legal.g2.com/terms-of-use">
              {({ onClick }) => (
                <U21Button color="primary" onClick={onClick} variant="text">
                  Terms of Use
                </U21Button>
              )}
            </U21ExternalLinkModal>
            .
          </StyledU21Typography>
          <Footer horizontal>
            <U21Button
              onClick={() => {
                setPopupOpen(false);
                setTimestamp(new Date().toISOString());
              }}
              tooltip={
                <>
                  <span>Click </span>
                  <G2SVG
                    height={20}
                    width={20}
                    style={{ top: 5, position: 'relative' }}
                  />
                  <span> in the header to access this later</span>
                </>
              }
              size="small"
              variant="ghost"
            >
              Snooze
            </U21Button>
            <StyledU21Button
              color="primary"
              loading={isPending}
              onClick={async () => {
                try {
                  const response = await generateG2State();
                  setState(response.state);
                  setPopupOpen(false);
                  setModalOpen(true);
                } catch {
                  dispatch(
                    sendErrorToast(
                      'Failed to load review form. Please try again.',
                    ),
                  );
                }
              }}
              size="small"
              variant="contained"
            >
              Write a Review
            </StyledU21Button>
          </Footer>
        </Content>
      </U21MenuLayout>
      <U21Modal
        closeButtonProps={{ children: 'Close' }}
        onClose={() => {
          setModalOpen(false);
          setTimestamp(new Date().toISOString());
        }}
        open={modalOpen}
        size="fullscreen"
        title="Tell us what you think!"
        titleIcon={<G2SVG />}
      >
        {state && (
          <Iframe
            src={`https://www.g2.com/partnerships/Unit21/users/login.embed?state=${state}&email=${encodeURIComponent(email)}`}
          />
        )}
      </U21Modal>
    </>
  );
};

export const StyledBackdrop = styled(Backdrop)`
  // number chosen based on https://mui.com/material-ui/customization/z-index
  z-index: 1000;
`;

const Content = styled(U21Spacer)`
  max-width: 400px;
  padding: 8px;
`;

const Footer = styled(U21Spacer)`
  justify-content: flex-end;
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const StyledU21Typography = styled(U21Typography)`
  .MuiTypography-root {
    font-size: 10px;
  }
`;

const StyledU21Alert = styled(U21Alert)`
  .MuiAlert-icon {
    font-size: 18px;
    margin-top: -2px;
    margin-right: 8px;
  }
`;

const StyledU21Button = styled(U21Button)`
  text-transform: none;
`;
